import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from "@angular/core";
import { DxDataGridComponent } from "devextreme-angular";
import { custom } from "devextreme/ui/dialog";
import { Subscription, forkJoin } from "rxjs";
import { Addition } from "src/app/_api/models/addition.model";
import { AdditionsService } from "src/app/_api/services/additions.service";
import { AdminRoleService } from "src/app/_api/services/role.service";
import { TimeService } from "src/app/modules/time/_api/services/time.service";
import { StatusUpdateEventService } from "src/app/modules/time/timesalaryapproval/status-update-event.service";
import { TimesalaryapprovalDetailTimeComponent } from "src/app/modules/time/timesalaryapproval/timesalaryapproval-detail/timesalaryapproval-detail-time/timesalaryapproval-detail-time.component";
import { AmsNotifierService } from "src/app/shared/services/ams-notifier.service";
import { DateRangeEventService } from "src/app/shared/services/date-range-event.service";
import { LanguageService } from "src/app/shared/services/language.service";
import { UserService } from "src/app/shared/services/user.service";
import * as moment from 'moment';
import { PortalTypeService } from "../../../../../../shared/services/portal-type.service";

@Component({
  selector: "app-project-portal-approval-employee-additions",
  templateUrl: "./additions.component.html",
  styleUrls: ["./additions.component.css"],
})
export class ProjectPortalApprovalEmployeeAdditionsComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() portalType: string;
  @Input() additions: Addition[];
  @Input() additionTypes: any[];
  @Input() selectedTimeRecord: any;
  @Input() allowEdit: boolean;
  @Input() timeSalaryTimeComponent: TimesalaryapprovalDetailTimeComponent;
  @Input() selectedProjectId: number;
  @Input() boxHeight: number; 
  @Input() projectReadAll: boolean = false; 

  @Output() isBusy = new EventEmitter<boolean>();
  @Output() refresh = new EventEmitter<boolean>(); //false if not clearing datagrid selections
  @Output() editingStart = new EventEmitter<any>();
  @Output() loading = new EventEmitter<boolean>(); //emit event to parent(project-portal.component) to show load indicator
  @Output() cellPrepared = new EventEmitter<any>();

  additionsNotConnectedToTime: Addition[] = [];
  filteredAdditions: Addition[] = [];

  additionText: string;
  isClone: boolean = false;
  cloneData: any;

  @ViewChild("datagriddx", { static: false })
  additionsGrid: DxDataGridComponent;

  dateRangeEventSubscription: Subscription;
  dateInterval: any = { FromDate: moment(), ToDate: moment() };

  approvalFunction: number;
  headerFilterData = []; 

  constructor(
    public languageService: LanguageService,
    private additionsService: AdditionsService,
    private amsNotifierService: AmsNotifierService,
    private dateRangeEventService: DateRangeEventService,
    private statusUpdateEventService: StatusUpdateEventService,
    private userService: UserService,
    private timeService: TimeService,
    private roleService: AdminRoleService,
    private portalTypeService: PortalTypeService) {

    this.dateRangeEventSubscription = this.dateRangeEventService.onDateRangeChanged().subscribe((dateInterval) => {
      this.dateInterval = dateInterval;
      this.loadAdditionsNotConnectedToTime();
    });

    this.statusUpdateEventService.onApproveRequested().subscribe(() => {
      this.approve();
    });

    this.statusUpdateEventService.onResetRequested().subscribe((statusId: number) => {
      this.reset(statusId);
    });
  }

  ngAfterViewInit() {
    this.approvalFunction = this.portalTypeService.isProjectPortal(this.portalType) ? 2 : 1;
    this.loadStatuses(); 
  }

  ngOnChanges(changes: SimpleChanges) {
    const hasChangedSelectedTimeRecord = changes.selectedTimeRecord !== undefined && changes.selectedTimeRecord.previousValue !== changes.selectedTimeRecord.currentValue;
    const hasChangedAdditions = changes.additions !== undefined && changes.additions.previousValue !== changes.additions.currentValue;
    const hasChangedProjectId = changes.selectedProjectId !== undefined && changes.selectedProjectId.previousValue !== changes.selectedProjectId.currentValue;

    if (hasChangedSelectedTimeRecord === false && hasChangedAdditions === false && hasChangedProjectId === false) { return; }

    if(hasChangedProjectId) this.loadAdditionsNotConnectedToTime();

    this.filterAdditions();
  }

  ngOnDestroy() {
    this.dateRangeEventSubscription.unsubscribe();
  }

  getSelectedRows() {
    return this.additionsGrid.instance.getSelectedRowKeys();
  }

  getSelectedRowsData() {
    return this.additionsGrid.instance.getSelectedRowsData();
  }

  selectAll() {
    this.additionsGrid.instance.selectAll();
  }

  deselectAll() {
    this.additionsGrid.instance.deselectAll();
  }

  async loadAdditionsNotConnectedToTime() {
    if (!this.dateInterval) return;

    this.additionsNotConnectedToTime = this.selectedProjectId > 0 ?
      await this.additionsService.getByProjectWithinPeriod(this.selectedProjectId, this.dateInterval.FromDate, this.dateInterval.ToDate).toPromise() :
      await this.additionsService.getNotConnectedToTimeWithinPeriod(this.dateInterval.FromDate, this.dateInterval.ToDate, this.projectReadAll).toPromise();
    this.filterAdditions();
  }

  private async loadAdditions(): Promise<any> {
    if (this.portalTypeService.isProjectPortal(this.portalType)) {
      this.additions = await this.additionsService
        .getByTime(this.selectedTimeRecord.TimeId)
        .toPromise();
    }
    else {
      this.additions = await this.additionsService
        .getByTimeEmployeeWithinPeriod(this.selectedTimeRecord.TimeId, this.selectedTimeRecord.EmployeeId, this.dateInterval.FromDate, this.dateInterval.ToDate)
        .toPromise();
    }

    this.filterAdditions();
  }

  private filterAdditions() {
    if (this.selectedTimeRecord) {
      this.filteredAdditions = this.additions.filter(a => a.EmployeeId === this.selectedTimeRecord.EmployeeId && a.Date === this.selectedTimeRecord.Date);
      return;
    }

    this.filteredAdditions = this.userService.account.IsAdmin || this.userService.account.IsSupervisor || this.projectReadAll
      ? this.additionsNotConnectedToTime  
      : this.additionsNotConnectedToTime.filter(a => a.EmployeeId === this.userService.account.EmployeeId);
  }

  onToolbarPreparing(e: any) {
    if (this.allowEdit) {
      e.toolbarOptions.items.unshift(
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "copy",
            title: this.languageService.LNG("Label.Copy"),
            onClick: this.onCopySelected.bind(this),
          },
        },
        {
          location: "after",
          widget: "dxButton",
          options: {
            icon: "trash",
            title: this.languageService.LNG("Label.Delete"),
            onClick: this.onDeleteSelected.bind(this),
          },
        }
      );
    }
  }

  onCopySelected() {
    let selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();
    if (selectedRowsData.length > 0) {
      for (let i = 0; i < selectedRowsData.length; i++) {
        this.isClone = true;
        this.cloneData = selectedRowsData[i];
        this.additionsGrid.instance.addRow();
      }
    }
  }

  onDeleteSelected() {
    let myDialog = custom({
      title: this.languageService.LNG("Label.Delete"),
      messageHtml: this.languageService.LNG(
        "Label.Do.You.Want.To.Delete.The.Selected.Rows"
      ),
      buttons: [
        {
          text: this.languageService.LNG("Label.Yes"),
          onClick: (e) => {
            return true;
          },
        },
        {
          text: this.languageService.LNG("Label.No"),
          onClick: (e) => {
            return false;
          },
        },
      ],
    });
    myDialog.show().then((confirmed: boolean) => {
      if (!confirmed) { return; }

      const additionIds: number[] = [];
      const selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();

      if (selectedRowsData.length === 0)
      {
        this.amsNotifierService.info(this.languageService.LNG("Label.Please.Select.Rows.To.Delete"));
        return;
      }

      for (let i = 0; i < selectedRowsData.length; i++) {
        additionIds.push(selectedRowsData[i].AdditionId);
      }

      if (additionIds.length === 0) { return; }

      this.onLoading(true);
      this.additionsService.deleteByAdditionIds(additionIds).subscribe(
        (result: any) => {
          this.onLoading(false);
          this.selectedTimeRecord ? this.loadAdditions() : this.loadAdditionsNotConnectedToTime();

          if (result.IsSuccess) {
            this.amsNotifierService.success(this.languageService.LNG("Label.DeletedOK"));

            if (result.Message != null && result.Message.length > 0) {
              this.amsNotifierService.error(result.Message);
            }

            this.refresh.emit(false);
            this.additionsGrid.selectedRowKeys = [];
          } else {
            this.amsNotifierService.error(result.ErrorMessage);
          }
        },
        (error) => {
          this.onLoading(false);
        }
      );
    });
  }

  onEditorPreparing(e: any) {
    if (e.parentType == "dataRow") {
      e.editorOptions.onFocusIn = function (args) {
        const input = args.element.querySelector(".dx-texteditor-input");
        if (input !== null && input !== undefined) {
          input.select();
        }
      };
    }
  }

  onEditingStart(e: any) {
    this.editingStart.emit(e);
  }

  onSavingMultiple(e: any) {
    e.cancel = true;

    const newAdditions = [];
    const updatedAdditions = [];

    this.onLoading(true);

    e.changes.forEach((row: any) => {
      const addition = this.selectedTimeRecord ? this.additionRecordConnectedToTime(row) : this.additionRecordNotConnectedToTime(row);

      if (row.type === "insert") {
        newAdditions.push(addition);
      }
      if (row.type === "update") {
        updatedAdditions.push(addition);
      }
    });

    this.additionsService
      .saveMultipleAdditions(newAdditions, updatedAdditions)
      .subscribe((result: any) => {
        if (result.IsSuccess) {
          this.onLoading(false);
          this.selectedTimeRecord ? this.loadAdditions() : this.loadAdditionsNotConnectedToTime();

          this.amsNotifierService.success(this.languageService.LNG("Label.SavedOK"));
          if (result.Message != null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }
          e.component.cancelEditData();
        } else {
          this.amsNotifierService.error(result.ErrorMessage);
        }
      },
      (error) => {
      }
    );

    e.component.refresh(true);
    e.component.cancelEditData();
  }

  additionRecordConnectedToTime(row: any) {
    return {
      AdditionId: row.type == "insert" ? -1 : row.key.AdditionId,
      AdditionTypeId: row.data.AdditionTypeId ? row.data.AdditionTypeId : row.key.AdditionTypeId,
      Value: row.data.Value ? row.data.Value : row.key.Value,
      CarrierId: this.selectedTimeRecord.CarrierId,
      EmployeeId: this.selectedTimeRecord.EmployeeId,
      CategoryId: this.selectedTimeRecord.CategoryId,
      Date: this.selectedTimeRecord.Date,
      Comment: row.data.Comment !== undefined ? row.data.Comment : row.key.Comment
    };
  }

  additionRecordNotConnectedToTime(row: any) {
    return {
      AdditionId: row.type == "insert" ? -1 : row.key.AdditionId,
      StatusId: row.data.StatusId ? row.data.StatusId : row.key.StatusId,
      AdditionTypeId: row.data.AdditionTypeId ? row.data.AdditionTypeId : row.key.AdditionTypeId,
      Value: row.data.Value ? row.data.Value : row.key.Value,
      CarrierId: row.data.CarrierId ? row.data.CarrierId : row.key.CarrierId,
      EmployeeId: row.data.EmployeeId ? row.data.EmployeeId : row.key.EmployeeId,
      CategoryId: this.portalTypeService.isProjectPortal(this.portalType) ? 9 : 6,
      Date: row.data.Date ? moment(row.data.Date).format() : row.key.Date,
      Comment: row.data.Comment !== undefined ? row.data.Comment : row.key.Comment
    };
  }

  onCellPrepared(e: any) {
    if (this.selectedTimeRecord) { return; }
    this.cellPrepared.emit(e);
  }

  onProjectValueChanged(e: any, cell: any) {
    const selectedProject = e.component.option("selectedItem");
    if (selectedProject) {
      cell.setValue(selectedProject);
    }
  }

  setProjectValue(newData: any, value: any) {
    newData.CarrierId = value && value.CarrierId;
    newData.ProjectNumberAndName = value && value.ProjectDisplayName;
  }

  async onInitNewRow(e: any) {
    this.selectedTimeRecord ? this.initNewRowConnectedToTime(e) : this.initNewRowConnectedNotConnectedToTime(e);
    this.isClone = false;
  }

  private initNewRowConnectedToTime(e: any) {
    if (!this.isClone) {
      e.data.AdditionId = -1;
      e.data.CarrierId = this.selectedTimeRecord.CarrierId;
    } else {
      e.data.AdditionId = this.cloneData.AdditionId;
      e.data.AdditionTypeId = this.cloneData.AdditionTypeId;
      e.data.Value = this.cloneData.Value;
      e.data.UnitDescription = this.cloneData.UnitDescription;
      e.data.Comment = this.cloneData.Comment;
    }
  }

  private initNewRowConnectedNotConnectedToTime(e: any) {
    if (!this.isClone) {
      e.data.AdditionId = -1;
      e.data.StatusId = 3;
    } else {
      e.data.AdditionId = this.cloneData.AdditionId;
      e.data.StatusId = this.cloneData.StatusId;
      e.data.AdditionTypeId = this.cloneData.AdditionTypeId;
      e.data.CarrierId = this.cloneData.CarrierId;
      e.data.ProjectNumberAndName = this.cloneData.ProjectNumberAndName;
      e.data.EmployeeId = this.cloneData.EmployeeId;
      e.data.Date = this.cloneData.Date;
      e.data.Value = this.cloneData.Value;
      e.data.UnitDescription = this.cloneData.UnitDescription;
      e.data.Comment = this.cloneData.Comment;
    }
  }

  setAllowEdit(allow: boolean) {
    this.allowEdit = allow;
  }

  onLoading(loading: boolean) {
    this.loading.emit(loading);
  }

  private approve() {
    if (this.selectedTimeRecord) { return; }

    const selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();

    if (selectedRowsData.length === 0) { return; }

    this.onLoading(true);

    const additionIds: number[] = [];

    for (let i = 0; i < selectedRowsData.length; i++) {
      if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
        selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
        (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
          continue;
      }
      additionIds.push(selectedRowsData[i].AdditionId);
    }

    if (additionIds.length === 0) { return; }

    if (additionIds.length > 100) {
      let startIndex = 0;
      let endIndex = 100;
      let arrayRequests = [];

      while (endIndex <= additionIds.length) {
        let additionIdsSliced = additionIds.slice(startIndex, endIndex);

        if (additionIdsSliced.length === 0) {
          break;
        }

        let requestConfirm = this.timeService.approveAdditionByAdditionIds(additionIdsSliced, this.approvalFunction);
        arrayRequests.push(requestConfirm);
        startIndex = endIndex;
        endIndex = endIndex + 100 > additionIds.length ? additionIds.length : endIndex + 100;
        if (startIndex > additionIds.length) {
          break;
        }
      }
      forkJoin(arrayRequests).subscribe((results: any) => {

        this.onLoading(false);
        let bAllOk = true;
        for(let result of results){
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
            bAllOk = false;
          }
        }
        if (bAllOk) {
          let okMessage = this.languageService.LNG("Label.Addition") + " " + this.languageService.LNG("Label.Is.Approved");
          this.amsNotifierService.success(okMessage);
          this.additionsGrid.selectedRowKeys = [];
        }

        this.selectedTimeRecord ? this.loadAdditions() : this.loadAdditionsNotConnectedToTime();
      });
    }
    else {
      this.timeService.approveAdditionByAdditionIds(additionIds, this.approvalFunction).subscribe((result: any) => {

        this.onLoading(false);

        if (result.IsSuccess) {
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }
          this.additionsGrid.selectedRowKeys = [];
          let okMessage = this.languageService.LNG("Label.Addition") + " " + this.languageService.LNG("Label.Is.Approved");
          this.amsNotifierService.success(okMessage);

          this.selectedTimeRecord ? this.loadAdditions() : this.loadAdditionsNotConnectedToTime();
        }
        else {
          this.amsNotifierService.error(result.ErrorMessage);
        }
      }, error => {
        this.onLoading(false);
      });
    }
  }

  private reset(statusId: number) {
    if (this.selectedTimeRecord) { return; }

    const selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();

    if (selectedRowsData.length === 0) { return; }

    this.onLoading(true);

    const additionIds: number[] = [];

    for (let i = 0; i < selectedRowsData.length; i++) {
      if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
        selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
        (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
          continue;
      }
      additionIds.push(selectedRowsData[i].AdditionId);
    }

    if (additionIds.length === 0) { return; }

    if (additionIds.length > 100) {
      let startIndex = 0;
      let endIndex = 100;
      let arrayRequests = [];

      while (endIndex <= additionIds.length) {
        let additionIdsSliced = additionIds.slice(startIndex, endIndex);
        if (additionIdsSliced.length === 0) {
          break;
        }

        let requestReset = this.timeService.resetAdditionByAdditionIds(additionIdsSliced, statusId, this.approvalFunction);
        arrayRequests.push(requestReset);
        startIndex = endIndex;
        endIndex = endIndex + 100 > additionIds.length ? additionIds.length : endIndex + 100;
        if (startIndex > additionIds.length) {
          break;
        }
      }
      forkJoin(arrayRequests).subscribe((results: any) => {

        this.onLoading(false)
        let bAllOk = true;
        for(let result of results){
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
            bAllOk = false;
          }
        }
        if (bAllOk) {
          this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
          this.additionsGrid.selectedRowKeys = [];
        }

        this.selectedTimeRecord ? this.loadAdditions() : this.loadAdditionsNotConnectedToTime();
      });
    }
    else {
      this.timeService.resetAdditionByAdditionIds(additionIds, statusId, this.approvalFunction).subscribe((result: any) => { // success path

        this.onLoading(false);

        if (result.IsSuccess) {
          if (result.Message !== null && result.Message.length > 0) {
            this.amsNotifierService.error(result.Message);
          }
          else {
            this.additionsGrid.selectedRowKeys = [];
            this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
          }

          this.selectedTimeRecord ? this.loadAdditions() : this.loadAdditionsNotConnectedToTime();
        }
        else {
          this.amsNotifierService.error(result.ErrorMessage);
        }
      }, error => {
        this.onLoading(false)
      });
    }
  }

  private async loadStatuses() {
    const result = await this.roleService.getStatuses().toPromise();

    result.forEach(status => {
      if(status.StatusId >= 3) {
        this.headerFilterData.push( {
          text: status.LanguageTagLabel !== "" ? this.languageService.LNG(status.LanguageTagLabel) : status.Code,
          value: ["StatusId", "=", status.StatusId]
        });
      }
    });
  }

  //prevent all rows from being confirmed when none are selected if the page has multiple grids. Then some of the other grids may have selected rows
  onConfirm(preventConfirmAll: boolean = false): Promise<boolean> {
    this.loading.emit(true);

    return new Promise(resolve => {
      let additionIds: number[] = [];
      let selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();
      if (selectedRowsData.length == 0 && !preventConfirmAll) {
        this.additions.forEach(addition => {
          selectedRowsData.push(addition);
        });
      }

      if (selectedRowsData.length > 0) {
        for (let i = 0; i < selectedRowsData.length; i++) {
          // If user is supervisor and the time post has another SupervisorId, then make it unapprovable for user
          if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
            selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
            (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
              continue;
          }
          additionIds.push(selectedRowsData[i].AdditionId);
        }

        if (additionIds.length > 0) {
          if (additionIds.length > 100) {
            let startIndex = 0;
            let endIndex = 100;
            let arrayRequests = [];
            while (endIndex <= additionIds.length) {
              let additionIdsSliced = additionIds.slice(startIndex, endIndex);
              if (additionIdsSliced.length === 0) {
                break;
              }

              let requestConfirm = this.timeService.confirmAdditionByAdditionIds(additionIdsSliced);
              arrayRequests.push(requestConfirm);
              startIndex = endIndex;
              endIndex = endIndex + 100 > additionIds.length ? additionIds.length : endIndex + 100;
              if (startIndex > additionIds.length) {
                break;
              }
            }
            forkJoin(arrayRequests).subscribe((results: any) => {

              this.loading.emit(false);
              let bAllOk = true;
              for(let result of results){
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                  bAllOk = false;
                }
              }
              if (bAllOk) {
                let okMessage = this.languageService.LNG("Label.Addition") + " " + this.languageService.LNG("Label.Is.Confirmed");
                this.amsNotifierService.success(okMessage);
                this.additionsGrid.selectedRowKeys = [];
              }
              resolve(true);
            });
          }
          else {
            this.timeService.confirmAdditionByAdditionIds(additionIds).subscribe((result: any) => { // success path

              this.loading.emit(false);

              if (result.IsSuccess) {
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                }
                this.additionsGrid.selectedRowKeys = [];
                let okMessage = this.languageService.LNG("Label.Addition") + " " + this.languageService.LNG("Label.Is.Confirmed");
                this.amsNotifierService.success(okMessage);
                resolve(true);
              }
              else {
                this.amsNotifierService.error(result.ErrorMessage);
                resolve(false);
              }
            }, error => { // error path;
              this.loading.emit(false);
              resolve(false);
            });
          }
        }
        else {
          this.amsNotifierService.info(this.languageService.LNG("Label.No.Addition.Posts.To.Confirm"));
          this.loading.emit(false);
          resolve(false);
        }
      }
      else {
        resolve(false);
      }
    })
  }

  //prevent all rows from being approved when none are selected if the page has multiple grids. Then some of the other grids may have selected rows
  onApprove(preventApproveAll: boolean = false): Promise<boolean> {
    this.loading.emit(true);
    
    return new Promise(resolve => {
      let additionIds: number[] = [];
      let selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();
      if (selectedRowsData.length == 0 && !preventApproveAll) {
        this.additions.forEach(addition => {
          selectedRowsData.push(addition);
        });
      }

      if (selectedRowsData.length > 0) {
        for (let i = 0; i < selectedRowsData.length; i++) {
          //Eirik 11.06.21 v2.0.32.9 task 1200 - if user is supervisor and the time post has another SupervisorId, then make it unapprovable for user
          if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
            selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
            (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
              continue;
          }
          additionIds.push(selectedRowsData[i].AdditionId);
        }

        if (additionIds.length > 0) {
          if (additionIds.length > 100) {
            let startIndex = 0;
            let endIndex = 100;
            let arrayRequests = [];
            while (endIndex <= additionIds.length) {
              let additionIdsSliced = additionIds.slice(startIndex, endIndex);
              if (additionIdsSliced.length === 0) {
                break;
              }

              let requestConfirm = this.timeService.approveAdditionByAdditionIds(additionIdsSliced, this.approvalFunction);
              arrayRequests.push(requestConfirm);
              startIndex = endIndex;
              endIndex = endIndex + 100 > additionIds.length ? additionIds.length : endIndex + 100;
              if (startIndex > additionIds.length) {
                break;
              }
            }
            forkJoin(arrayRequests).subscribe((results: any) => {

              this.loading.emit(false);
              let bAllOk = true;
              for(let result of results){
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                  bAllOk = false;
                }
              }
              if (bAllOk) {
                let okMessage = this.languageService.LNG("Label.Addition") + " " + this.languageService.LNG("Label.Is.Approved");
                this.amsNotifierService.success(okMessage);
                this.additionsGrid.selectedRowKeys = [];
              }
              resolve(true);
            });
          }
          else {
            this.timeService.approveAdditionByAdditionIds(additionIds, this.approvalFunction).subscribe((result: any) => { // success path

              this.loading.emit(false);

              if (result.IsSuccess) {
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                }
                this.additionsGrid.selectedRowKeys = [];
                let okMessage = this.languageService.LNG("Label.Addition") + " " + this.languageService.LNG("Label.Is.Approved");
                this.amsNotifierService.success(okMessage);
                resolve(true);
              }
              else {
                this.amsNotifierService.error(result.ErrorMessage);
                resolve(false);
              }
            }, error => { // error path;
              this.loading.emit(false);
              resolve(false);
            });
          }
        }
        else {
          this.amsNotifierService.info(this.languageService.LNG("Label.No.Addition.Posts.To.Approve"));
          this.loading.emit(false);
          resolve(false);
        }
      }
      else {
        resolve(false);
      }
    })
  }

  onReset(statusId: number): Promise<boolean> {
    this.loading.emit(true);

    return new Promise(resolve => {
      let additionIds: number[] = [];
      let selectedRowsData = this.additionsGrid.instance.getSelectedRowsData();

      if (selectedRowsData.length > 0) {
        for (let i = 0; i < selectedRowsData.length; i++) {
          if (!this.userService.account.IsAdmin && selectedRowsData[i].SupervisorId != null && this.userService.account.IsSupervisor &&
            selectedRowsData[i].SupervisorId != this.userService.account.EmployeeId  &&
            (this.userService.account.IsAdmin || this.userService.account.IsSupervisor)) {
              continue;
          }
          additionIds.push(selectedRowsData[i].AdditionId);
        }

        if (additionIds.length > 0) {
          if (additionIds.length > 100) {
            let startIndex = 0;
            let endIndex = 100;
            let arrayRequests = [];
            while (endIndex <= additionIds.length) {
              let additionIdsSliced = additionIds.slice(startIndex, endIndex);
              if (additionIdsSliced.length === 0) {
                break;
              }

              let requestReset = this.timeService.resetAdditionByAdditionIds(additionIdsSliced, statusId, this.approvalFunction);
              arrayRequests.push(requestReset);
              startIndex = endIndex;
              endIndex = endIndex + 100 > additionIds.length ? additionIds.length : endIndex + 100;
              if (startIndex > additionIds.length) {
                break;
              }
            }
            forkJoin(arrayRequests).subscribe((results: any) => {

              this.loading.emit(false);
              let bAllOk = true;
              for(let result of results){
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                  bAllOk = false;
                }
              }
              if (bAllOk) {
                this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
                this.additionsGrid.selectedRowKeys = [];
              }
              resolve(true);
            });
          }
          else {
            this.timeService.resetAdditionByAdditionIds(additionIds, statusId, this.approvalFunction).subscribe((result: any) => { // success path

              this.loading.emit(false);

              if (result.IsSuccess) {
                if (result.Message !== null && result.Message.length > 0) {
                  this.amsNotifierService.error(result.Message);
                }
                else {
                  this.additionsGrid.selectedRowKeys = [];
                  this.amsNotifierService.success(this.languageService.LNG("Label.Reset.Ok"));
                }
                resolve(true);
              }
              else {
                this.amsNotifierService.error(result.ErrorMessage);
                resolve(false);
              }
            }, error => {
              this.loading.emit(false);
              resolve(false);
            });
          }
        }
        else {
          this.amsNotifierService.info(this.languageService.LNG("Label.No.Posts.To.Reset"));
          this.loading.emit(false);
          resolve(false);
        }
      }
      else {
        resolve(false);
      }
    });
  }
}
